import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TablePagination,Dialog,CircularProgress,useMediaQuery,IconButton,Menu,MenuItem,Tooltip}from"@mui/material";
import EditRoundedIcon from"@mui/icons-material/EditRounded";
import SyncRoundedIcon from"@mui/icons-material/SyncRounded";
import PrintRoundedIcon from"@mui/icons-material/PrintRounded";
import PictureAsPdfOutlinedIcon from"@mui/icons-material/PictureAsPdfOutlined";
import ReceiptLongOutlinedIcon from"@mui/icons-material/ReceiptLongOutlined";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import{useAuth}from"../../context/AuthContext";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import Filter from"./AdminDocuments/Filter";

const PRINT_INVOICE_ENDPOINT="http://127.0.0.1:5050/print-invoice";

function formatMoney(value){
  const number=Number(value||0);

  return number.toLocaleString("es-EC",{
    minimumFractionDigits:2,
    maximumFractionDigits:2,
  });
}

function getDocumentCode(factura){
  return factura.numero_documento||factura.numero||"-";
}

function getPersonaName(factura){
  return(
    factura.persona?.nombre_comercial||
    factura.persona?.razon_social||
    factura.comprador_razon_social||
    "Consumidor Final"
  );
}

function getPersonaType(factura){
  const tipo=(
    factura.persona?.tipo||
    factura.persona?.tipo_persona||
    factura.persona?.rol||
    ""
  );

  if(String(tipo).toLowerCase().includes("proveedor")){
    return"Proveedor";
  }

  return"Cliente";
}

function getEstadoVisual(factura){
  const backend=String(factura.estado_visual||"").trim().toUpperCase();

  if(backend==="AUTORIZADA"){
    return"Autorizada";
  }

  if(backend==="DEVUELTA"){
    return"Devuelta";
  }

  if(backend==="EMITIDA"){
    return"Emitida";
  }

  const estado=String(factura.estado||"").trim().toUpperCase();

  const sri=String(
    factura.sri?.estado||
    factura.sri_estado||
    ""
  ).trim().toUpperCase();

  if(
    estado==="AUTORIZADA"||
    sri==="AUTORIZADO"||
    sri==="AUTORIZADA"
  ){
    return"Autorizada";
  }

  if(
    estado==="NO_AUTORIZADA"||
    sri==="NO_AUTORIZADO"||
    sri==="NO_AUTORIZADA"||
    sri==="DEVUELTA"
  ){
    return"Devuelta";
  }

  return"Emitida";
}

function getStatusStyles(theme,estado){
  if(estado==="Autorizada"){
    return{
      bg:"#237A36",
      color:"#FFFFFF",
      border:"#237A36",
    };
  }

  if(estado==="Devuelta"){
    return{
      bg:"#B91C1C",
      color:"#FFFFFF",
      border:"#B91C1C",
    };
  }

  return{
    bg:theme.theme_mode==="dark"
      ?"rgba(56,189,248,0.16)"
      :"#E0F2FE",
    color:theme.theme_mode==="dark"
      ?"#7DD3FC"
      :"#0369A1",
    border:theme.theme_mode==="dark"
      ?"rgba(125,211,252,0.40)"
      :"#7DD3FC",
  };
}

function EstadoBadge({factura,theme}){
  const estado=getEstadoVisual(factura);
  const styles=getStatusStyles(theme,estado);

  return(
    <Box
      component="span"
      sx={{
        display:"inline-flex",
        alignItems:"center",
        px:1.15,
        py:.5,
        borderRadius:"8px",
        bgcolor:styles.bg,
        color:styles.color,
        border:`1px solid ${styles.border}`,
        fontSize:12,
        fontWeight:700,
        lineHeight:1,
        whiteSpace:"nowrap",
      }}
    >
      {estado}
    </Box>
  );
}

function normalizePermissionCodes(value){
  if(!Array.isArray(value)){
    return[];
  }

  return value.map(item=>{
    if(typeof item==="string"){
      return item.trim();
    }

    if(item?.code){
      return String(item.code).trim();
    }

    if(item?.permission?.code){
      return String(item.permission.code).trim();
    }

    if(item?.permission_code){
      return String(item.permission_code).trim();
    }

    return"";
  }).filter(Boolean);
}

function getFilenameFromDisposition(response,fallback){
  const disposition=response.headers.get("Content-Disposition");

  if(!disposition){
    return fallback;
  }

  const utfMatch=disposition.match(/filename\*=UTF-8''([^;]+)/i);

  if(utfMatch?.[1]){
    try{
      return decodeURIComponent(
        utfMatch[1].replace(/["']/g,"")
      );
    }catch{
      return utfMatch[1].replace(/["']/g,"");
    }
  }

  const normalMatch=disposition.match(/filename="?([^"]+)"?/i);

  if(normalMatch?.[1]){
    return normalMatch[1].trim();
  }

  return fallback;
}

async function getErrorMessage(response,fallback){
  try{
    const contentType=response.headers.get("Content-Type")||"";

    if(contentType.includes("application/json")){
      const data=await response.json();

      return(
        data.error||
        data.detail||
        data.message||
        fallback
      );
    }

    const text=await response.text();

    if(text){
      return text;
    }
  }catch{
    return fallback;
  }

  return fallback;
}

export default function AdminDocuments(){
  const[sidebarOpen,setSidebarOpen]=useState(true);

  const{dashboardTheme}=useDashboardTheme();

  const{showError,showSuccess}=useAlert();

  const{user,accessToken}=useAuth();

  const isMobile=useMediaQuery("(max-width:900px)");

  const[documents,setDocuments]=useState([]);

  const[search,setSearch]=useState("");

  const[desde,setDesde]=useState("");

  const[hasta,setHasta]=useState("");

  const[estado,setEstado]=useState("");

  const[loading,setLoading]=useState(false);

  const[reportDownloading,setReportDownloading]=useState(false);

  const[backupDownloading,setBackupDownloading]=useState(false);

  const[processingSri,setProcessingSri]=useState(false);

  const[printingTicket,setPrintingTicket]=useState(false);

  const[downloadingPdf,setDownloadingPdf]=useState(false);

  const[processingDocument,setProcessingDocument]=useState(null);

  const[actionAnchor,setActionAnchor]=useState(null);

  const[actionDocument,setActionDocument]=useState(null);

  const[printAnchor,setPrintAnchor]=useState(null);

  const[printDocument,setPrintDocument]=useState(null);

  const[page,setPage]=useState(0);

  const rowsPerPage=isMobile?10:30;

  const token=accessToken;

  const busy=
    reportDownloading||
    backupDownloading||
    processingSri||
    printingTicket||
    downloadingPdf;

  const permissions=useMemo(()=>{
    return normalizePermissionCodes(user?.permissions);
  },[user?.permissions]);

  const isSuperAdmin=
    user?.role==="super_admin"||
    user?.is_superuser===true;

  const isAdmin=
    user?.role==="admin"||
    user?.is_admin===true;

  const canReports=
    isSuperAdmin||
    isAdmin||
    permissions.includes("facturacion.reports");

  const canEditFactura=
    isSuperAdmin||
    isAdmin||
    permissions.includes("facturacion.edit");

  const closeActionMenu=()=>{
    setActionAnchor(null);
    setActionDocument(null);
  };

  const openActionMenu=(event,documento)=>{
    if(busy){
      return;
    }

    setActionAnchor(event.currentTarget);
    setActionDocument(documento);
  };

  const closePrintMenu=()=>{
    setPrintAnchor(null);
    setPrintDocument(null);
  };

  const openPrintMenu=(event,documento)=>{
    if(busy){
      return;
    }

    setPrintAnchor(event.currentTarget);
    setPrintDocument(documento);
  };

  const loadDocuments=async()=>{
    try{
      setLoading(true);

      const params=new URLSearchParams();

      params.set("page","1");
      params.set("page_size","100");

      if(search.trim()){
        params.set("search",search.trim());
      }

      if(desde){
        params.set("desde",desde);
      }

      if(hasta){
        params.set("hasta",hasta);
      }

      if(estado){
        params.set("estado",estado);
      }

      const response=await fetch(
        `/api/facturacion/documentos/facturas/?${params.toString()}`,
        {
          method:"GET",
          headers:{
            Authorization:`Bearer ${token}`,
          },
        }
      );

      let data={};

      try{
        data=await response.json();
      }catch{
        data={};
      }

      if(!response.ok){
        showError(
          data.error||
          data.detail||
          "No se pudieron cargar los documentos."
        );

        setDocuments([]);

        return;
      }

      setDocuments(
        Array.isArray(data.results)
          ?data.results
          :[]
      );
    }catch(error){
      console.error("ERROR CARGANDO DOCUMENTOS:",error);

      showError("Error de conexión.");

      setDocuments([]);
    }finally{
      setLoading(false);
    }
  };

  const downloadBlobResponse=async(response,fallbackFilename)=>{
    const blob=await response.blob();

    const filename=getFilenameFromDisposition(
      response,
      fallbackFilename
    );

    const objectUrl=window.URL.createObjectURL(blob);

    const link=document.createElement("a");

    link.href=objectUrl;
    link.download=filename;

    document.body.appendChild(link);

    link.click();

    link.remove();

    window.URL.revokeObjectURL(objectUrl);
  };

  const downloadReport=async()=>{
    try{
      setReportDownloading(true);

      const params=new URLSearchParams();

      params.set("fecha_inicio",desde||"");
      params.set("fecha_fin",hasta||"");

      const response=await fetch(
        `/api/facturacion/reportes/ventas/excel/?${params.toString()}`,
        {
          method:"GET",
          headers:{
            Authorization:`Bearer ${token}`,
          },
        }
      );

      if(!response.ok){
        const message=await getErrorMessage(
          response,
          "No se pudo descargar el reporte."
        );

        showError(message);

        return;
      }

      await downloadBlobResponse(
        response,
        `Reporte_Ventas_${desde||"sin-fecha"}_${hasta||"sin-fecha"}.xlsx`
      );
    }catch(error){
      console.error("ERROR DESCARGANDO EXCEL:",error);

      showError(
        "Error de conexión al descargar el reporte."
      );
    }finally{
      setReportDownloading(false);
    }
  };

  const downloadBackup=async()=>{
    try{
      setBackupDownloading(true);

      const params=new URLSearchParams();

      params.set("fecha_inicio",desde||"");
      params.set("fecha_fin",hasta||"");

      const response=await fetch(
        `/api/facturacion/reportes/ventas/respaldo/?${params.toString()}`,
        {
          method:"GET",
          headers:{
            Authorization:`Bearer ${token}`,
          },
        }
      );

      if(!response.ok){
        const message=await getErrorMessage(
          response,
          "No se pudieron descargar las facturas."
        );

        showError(message);

        return;
      }

      await downloadBlobResponse(
        response,
        `Respaldo_Ventas_${desde||"sin-fecha"}_${hasta||"sin-fecha"}.zip`
      );
    }catch(error){
      console.error("ERROR DESCARGANDO FACTURAS:",error);

      showError(
        "Error de conexión al descargar las facturas."
      );
    }finally{
      setBackupDownloading(false);
    }
  };

  const downloadFacturaPdf=async()=>{
    if(!printDocument||busy){
      return;
    }

    const documento=printDocument;

    closePrintMenu();

    if(!documento.id){
      showError("No se pudo identificar la factura.");
      return;
    }

    try{
      setProcessingDocument(documento);

      setDownloadingPdf(true);

      const response=await fetch(
        `/api/facturacion/facturas/${documento.id}/pdf/`,
        {
          method:"GET",
          headers:{
            Authorization:`Bearer ${token}`,
          },
        }
      );

      if(!response.ok){
        const message=await getErrorMessage(
          response,
          "No se pudo descargar el PDF."
        );

        showError(message);

        return;
      }

      const numero=getDocumentCode(documento)
        .replaceAll("/","-")
        .replaceAll("\\","-");

      await downloadBlobResponse(
        response,
        `Factura_${numero}.pdf`
      );
    }catch(error){
      console.error("ERROR DESCARGANDO PDF:",error);

      showError("No se pudo descargar el PDF.");
    }finally{
      setDownloadingPdf(false);

      setProcessingDocument(null);
    }
  };

  const printFacturaTicket=async()=>{
    if(!printDocument||busy){
      return;
    }

    const documento=printDocument;

    closePrintMenu();

    if(!documento.id){
      showError("No se pudo identificar la factura.");
      return;
    }

    try{
      setProcessingDocument(documento);

      setPrintingTicket(true);

      const ticketResponse=await fetch(
        `/api/facturacion/facturas/${documento.id}/ticket/`,
        {
          method:"GET",
          headers:{
            Authorization:`Bearer ${token}`,
          },
        }
      );

      let ticketData={};

      try{
        ticketData=await ticketResponse.json();
      }catch{
        ticketData={};
      }

      if(
        !ticketResponse.ok||
        !ticketData.ok||
        !ticketData.factura
      ){
        showError(
          ticketData.error||
          ticketData.detail||
          "No se pudieron obtener los datos de la factura."
        );

        return;
      }

      const printResponse=await fetch(
        PRINT_INVOICE_ENDPOINT,
        {
          method:"POST",
          headers:{
            "Content-Type":"application/json",
          },
          body:JSON.stringify(ticketData.factura),
        }
      );

      const printData=await printResponse
        .json()
        .catch(()=>({}));

      if(!printResponse.ok||!printData.ok){
        showError(
          printData.error||
          "La impresora no aceptó la factura."
        );

        return;
      }

      showSuccess(
        printData.message||
        "Factura enviada a impresión."
      );
    }catch(error){
      console.error("ERROR IMPRIMIENDO TICKET:",error);

      showError(
        "No se pudo conectar con el puente local de impresión en el puerto 5050."
      );
    }finally{
      setPrintingTicket(false);

      setProcessingDocument(null);
    }
  };

  const consultarReenviarSri=async()=>{
    if(!actionDocument||busy){
      return;
    }

    const documento=actionDocument;

    closeActionMenu();

    if(!documento.id){
      showError("No se pudo identificar la factura.");
      return;
    }

    try{
      setProcessingDocument(documento);

      setProcessingSri(true);

      const response=await fetch(
        `/api/facturacion/facturas/${documento.id}/consultar-reenviar-sri/`,
        {
          method:"POST",
          headers:{
            Authorization:`Bearer ${token}`,
            "Content-Type":"application/json",
          },
        }
      );

      let data={};

      try{
        data=await response.json();
      }catch{
        data={};
      }

      if(!response.ok){
        showError(
          data.detail||
          data.error||
          "No se pudo consultar o reenviar la factura al SRI."
        );

        return;
      }

      if(data.autorizada){
        showSuccess(
          data.mensaje||
          "Factura autorizada."
        );
      }else if(data.devuelta){
        showError(
          data.mensaje||
          "Factura devuelta por el SRI."
        );
      }else if(data.mensaje){
        showSuccess(data.mensaje);
      }

      await loadDocuments();
    }catch(error){
      console.error("ERROR CONSULTANDO SRI:",error);

      showError(
        "Error de conexión al consultar o reenviar la factura al SRI."
      );
    }finally{
      setProcessingSri(false);

      setProcessingDocument(null);
    }
  };

  useEffect(()=>{
    if(!token){
      return;
    }

    const delay=setTimeout(()=>{
      setPage(0);

      loadDocuments();
    },350);

    return()=>{
      clearTimeout(delay);
    };
  },[
    search,
    desde,
    hasta,
    estado,
    token,
  ]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const visibleDocuments=useMemo(()=>{
    const start=page*rowsPerPage;

    return documents.slice(
      start,
      start+rowsPerPage
    );
  },[
    documents,
    page,
    rowsPerPage,
  ]);

  const handleChangePage=(_event,newPage)=>{
    if(busy){
      return;
    }

    setPage(newPage);
  };

  const actionButtonSx={
    width:36,
    height:36,
    borderRadius:"9px",
    color:dashboardTheme.button_color,
    border:`1px solid ${dashboardTheme.border_color}`,
    bgcolor:dashboardTheme.card_background,
    "&:hover":{
      bgcolor:`${dashboardTheme.button_color}12`,
      borderColor:dashboardTheme.button_color,
    },
    "&.Mui-disabled":{
      opacity:.45,
    },
  };

  const menuPaperSx={
    mt:.7,
    minWidth:240,
    borderRadius:"12px",
    bgcolor:dashboardTheme.card_background,
    color:dashboardTheme.text_color,
    border:`1px solid ${dashboardTheme.border_color}`,
    boxShadow:"0 8px 28px rgba(0,0,0,0.14)",
  };

  return(
    <Box
      sx={{
        minHeight:"100vh",
        bgcolor:dashboardTheme.dashboard_background,
        display:"flex",
        color:dashboardTheme.text_color,
        overflowX:"hidden",
      }}
    >
      <Sidebar
        open={sidebarOpen}
        onClose={()=>setSidebarOpen(false)}
      />

      <Box
        sx={{
          flex:1,
          minWidth:0,
          minHeight:"100vh",
          display:"flex",
          flexDirection:"column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}
        />

        <Box
          component="main"
          sx={{
            flex:1,
            width:"100%",
            p:{
              xs:1.5,
              sm:2,
              md:3,
            },
          }}
        >
          <Box
            sx={{
              width:{
                xs:"100%",
                lg:"95%",
              },
              mx:"auto",
              mt:{
                xs:1,
                md:1.5,
              },
            }}
          >
            <Typography
              sx={{
                fontSize:{
                  xs:24,
                  sm:26,
                  md:30,
                },
                fontWeight:600,
                color:dashboardTheme.primary_color,
                lineHeight:1.2,
                mb:{
                  xs:1.5,
                  md:2,
                },
              }}
            >
              Documentos
            </Typography>

            <Paper
              elevation={0}
              sx={dashboardTheme.tableStyles.wrapper}
            >
              <Filter
                search={search}
                setSearch={setSearch}
                desde={desde}
                setDesde={setDesde}
                hasta={hasta}
                setHasta={setHasta}
                estado={estado}
                setEstado={setEstado}
                dashboardTheme={dashboardTheme}
                disabled={busy}
                canReports={canReports}
                onDownloadReport={downloadReport}
                onDownloadBackup={downloadBackup}
              />

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table
                    size="small"
                    sx={dashboardTheme.tableStyles.table}
                  >
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:120,
                          }}
                        >
                          Emisión
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:300,
                          }}
                        >
                          Persona
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:230,
                          }}
                        >
                          Documento
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:180,
                          }}
                        >
                          Estado
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={dashboardTheme.tableStyles.headCell}
                        >
                          Neto
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={dashboardTheme.tableStyles.headCell}
                        >
                          Imp.
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={dashboardTheme.tableStyles.headCell}
                        >
                          Total
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:130,
                          }}
                        >
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleDocuments.map(documento=>{
                        const documentCode=getDocumentCode(documento);

                        const persona=getPersonaName(documento);

                        const personaTipo=getPersonaType(documento);

                        const neto=Number(
                          documento.subtotal||
                          documento.total_sin_impuestos||
                          0
                        );

                        const impuesto=Number(
                          documento.iva||
                          documento.total_iva||
                          0
                        );

                        const total=Number(
                          documento.total||
                          0
                        );

                        return(
                          <TableRow
                            key={documento.id}
                            hover
                            sx={dashboardTheme.tableStyles.bodyRow}
                          >
                            <TableCell
                              sx={{
                                color:dashboardTheme.text_color,
                                fontWeight:700,
                                whiteSpace:"normal",
                                lineHeight:1.25,
                              }}
                            >
                              {documento.fecha_emision||"-"}
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize:14,
                                  fontWeight:700,
                                  color:dashboardTheme.text_color,
                                  lineHeight:1.25,
                                  textTransform:"uppercase",
                                }}
                              >
                                {persona}
                              </Typography>

                              <Typography
                                sx={{
                                  mt:.45,
                                  fontSize:12.5,
                                  fontWeight:500,
                                  color:dashboardTheme.theme_mode==="dark"
                                    ?"rgba(255,255,255,0.62)"
                                    :"#64748B",
                                  lineHeight:1.25,
                                }}
                              >
                                {personaTipo}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize:14,
                                  fontWeight:700,
                                  color:dashboardTheme.button_color,
                                  lineHeight:1.25,
                                }}
                              >
                                {documentCode}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <EstadoBadge
                                factura={documento}
                                theme={dashboardTheme}
                              />
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace:"nowrap",
                                color:dashboardTheme.text_color,
                                fontWeight:700,
                              }}
                            >
                              ${formatMoney(neto)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace:"nowrap",
                                color:dashboardTheme.text_color,
                                fontWeight:700,
                              }}
                            >
                              ${formatMoney(impuesto)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace:"nowrap",
                                color:dashboardTheme.text_color,
                                fontWeight:700,
                              }}
                            >
                              ${formatMoney(total)}
                            </TableCell>

                            <TableCell align="center">
                              <Box
                                sx={{
                                  display:"flex",
                                  alignItems:"center",
                                  justifyContent:"center",
                                  gap:.7,
                                }}
                              >
                                {canEditFactura&&(
                                  <Tooltip
                                    title="Acciones"
                                    arrow
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy}
                                        onClick={event=>openActionMenu(event,documento)}
                                        sx={actionButtonSx}
                                      >
                                        <EditRoundedIcon
                                          sx={{
                                            fontSize:20,
                                          }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}

                                <Tooltip
                                  title="Imprimir"
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={busy}
                                      onClick={event=>openPrintMenu(event,documento)}
                                      sx={actionButtonSx}
                                    >
                                      <PrintRoundedIcon
                                        sx={{
                                          fontSize:20,
                                        }}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {!loading&&visibleDocuments.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No hay documentos registrados
                          </TableCell>
                        </TableRow>
                      )}

                      {loading&&(
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Cargando documentos...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={documents.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={closeActionMenu}
        disableScrollLock
        anchorOrigin={{
          vertical:"bottom",
          horizontal:"right",
        }}
        transformOrigin={{
          vertical:"top",
          horizontal:"right",
        }}
        PaperProps={{
          sx:menuPaperSx,
        }}
        MenuListProps={{
          sx:{
            py:.6,
          },
        }}
      >
        <MenuItem
          onClick={consultarReenviarSri}
          sx={{
            minHeight:44,
            fontSize:14,
            fontWeight:600,
          }}
        >
          <SyncRoundedIcon
            sx={{
              mr:1.2,
              fontSize:20,
              color:dashboardTheme.primary_color,
            }}
          />
          Consultar / Reenviar al SRI
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={printAnchor}
        open={Boolean(printAnchor)}
        onClose={closePrintMenu}
        disableScrollLock
        anchorOrigin={{
          vertical:"bottom",
          horizontal:"right",
        }}
        transformOrigin={{
          vertical:"top",
          horizontal:"right",
        }}
        PaperProps={{
          sx:menuPaperSx,
        }}
        MenuListProps={{
          sx:{
            py:.6,
          },
        }}
      >
        <MenuItem
          onClick={downloadFacturaPdf}
          sx={{
            minHeight:44,
            fontSize:14,
            fontWeight:600,
          }}
        >
          <PictureAsPdfOutlinedIcon
            sx={{
              mr:1.2,
              fontSize:20,
              color:dashboardTheme.primary_color,
            }}
          />
          Descargar PDF
        </MenuItem>

        <MenuItem
          onClick={printFacturaTicket}
          sx={{
            minHeight:44,
            fontSize:14,
            fontWeight:600,
          }}
        >
          <ReceiptLongOutlinedIcon
            sx={{
              mr:1.2,
              fontSize:20,
              color:dashboardTheme.primary_color,
            }}
          />
          Imprimir ticket
        </MenuItem>
      </Menu>

      <Dialog
        open={busy}
        disableEscapeKeyDown
        disableScrollLock
        PaperProps={{
          sx:{
            width:"min(380px, calc(100vw - 32px))",
            borderRadius:"20px",
            bgcolor:dashboardTheme.card_background,
            border:`1px solid ${dashboardTheme.border_color}`,
            boxShadow:"none",
            p:3,
            textAlign:"center",
          },
        }}
        BackdropProps={{
          sx:{
            bgcolor:dashboardTheme.theme_mode==="dark"
              ?"rgba(0,0,0,0.72)"
              :"rgba(15,23,42,0.35)",
          },
        }}
      >
        <Box
          sx={{
            width:58,
            height:58,
            borderRadius:"50%",
            mx:"auto",
            mb:2,
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            bgcolor:`${dashboardTheme.primary_color}16`,
          }}
        >
          <CircularProgress
            size={30}
            thickness={4}
            sx={{
              color:dashboardTheme.primary_color,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize:20,
            fontWeight:700,
            color:dashboardTheme.text_color,
            lineHeight:1.2,
            mb:.8,
          }}
        >
          {reportDownloading
            ?"Generando reporte"
            :backupDownloading
              ?"Preparando facturas"
              :downloadingPdf
                ?"Generando PDF"
                :printingTicket
                  ?"Imprimiendo ticket"
                  :"Consultando SRI"}
        </Typography>

        <Typography
          sx={{
            fontSize:14,
            fontWeight:500,
            color:dashboardTheme.muted_text_color,
            lineHeight:1.5,
          }}
        >
          {reportDownloading
            ?"Se está preparando el archivo Excel."
            :backupDownloading
              ?"Se están recopilando XML, generando PDF y preparando el archivo ZIP."
              :downloadingPdf
                ?processingDocument
                  ?`Generando PDF de ${getDocumentCode(processingDocument)}.`
                  :"Generando PDF."
                :printingTicket
                  ?processingDocument
                    ?`Enviando ${getDocumentCode(processingDocument)} a la impresora.`
                    :"Enviando ticket a la impresora."
                  :processingDocument
                    ?`Consultando ${getDocumentCode(processingDocument)}.`
                    :"Consultando el estado del comprobante."}
        </Typography>
      </Dialog>
    </Box>
  );
}